/*
 * @providesModule MergeAccountCard
 *
 */

import {ImageSourcePropType, Text, TouchableOpacity, View} from 'react-native';
import SkillzImage from 'SkillzImage';
import {SkillzAvatar} from '@skillz/ui';
import styles from 'MergeAccountCardStyle';
import SkillzSVG from 'SkillzSVG';
import SkillzStyle from 'SkillzStyle';
import {_m} from 'INTL';

export type UserCardInfo = {
  userName: string;
  cashAmount: number;
  profilePic?: ImageSourcePropType;
};

export interface IMergeAccountCardProps {
  primary: boolean;
  userInfo: UserCardInfo;
  onSelect: (userAccount: UserCardInfo) => void;
  testID?: string;
  allowAccountSelection: boolean;
  isNewUI?: boolean;
}

export const TEST_IDS = {
  CONTAINER: 'merge-account-card-container',
  AVATAR_VIEW: 'merge-account-card-avatar-view',
  USER_NAME: 'merge-account-card-user-name',
  PRIMARY_TEXT: 'merge-account-card-primary-text',
  MERGE_TEXT: 'merge-account-card-merge-text',
};

const CASH_ICON_SIZE = 12;
const NEW_CASH_ICON_SIZE = 14;
const CHECK_MARK_ICON_SIZE = 16;
const MERGE_ICON_SIZE = 19;

export function MergeAccountCard(props: IMergeAccountCardProps) {
  const isNewUI = props.isNewUI;
  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;
  if (isNewUI) {
    return (
      <TouchableOpacity
        activeOpacity={props.allowAccountSelection ? 0.2 : 1}
        onPress={() => props.onSelect(props.userInfo)}
        testID={props.testID}
      >
        <View
          style={[
            styles.newContainer,
            !isDarkTheme
              ? {backgroundColor: 'rgba(0, 0, 0, 0.30)'}
              : {backgroundColor: '#ffffff30'},
          ]}
          testID={TEST_IDS.CONTAINER}
        >
          <View style={styles.avatarView} testID={TEST_IDS.AVATAR_VIEW}>
            <SkillzAvatar source={props.userInfo.profilePic} size={48} />
          </View>
          <View style={styles.leftContainer}>
            <Text
              allowFontScaling={false}
              style={[styles.newUserName, {color: '#ffffff'}]}
              testID={TEST_IDS.USER_NAME}
              numberOfLines={1}
              ellipsizeMode='tail'
            >
              {props.userInfo.userName}
            </Text>

            <View style={styles.currencyView}>
              <SkillzImage
                source={'cash_icon.svg'}
                height={NEW_CASH_ICON_SIZE}
                width={NEW_CASH_ICON_SIZE}
                style={styles.newCurrencyIcon}
              />

              <Text
                allowFontScaling={false}
                style={[styles.newCurrencyText, {color: '#ffffff'}]}
              >{`$${props.userInfo.cashAmount}`}</Text>
            </View>
          </View>

          <View style={styles.rightContainer}>
            {props.primary ? (
              <>
                <SkillzSVG
                  fill={SkillzStyle.fullStyles.profileIconGradient[0]}
                  source={'checkmark.svg'}
                  style={styles.newPrimaryIcon}
                />
                <Text
                  allowFontScaling={false}
                  style={[styles.newPrimaryText, {color: '#ffffff'}]}
                  testID={TEST_IDS.PRIMARY_TEXT}
                >
                  {_m('PRIMARY')}
                </Text>
              </>
            ) : (
              <>
                <SkillzSVG
                  fill={SkillzStyle.fullStyles.profileIconGradient[0]}
                  source={'mergeArrow.svg'}
                  style={styles.newMergeIcon}
                />
                <Text
                  allowFontScaling={false}
                  style={[styles.newMergeText, {color: '#ffffff'}]}
                  testID={TEST_IDS.MERGE_TEXT}
                >
                  {_m('MERGE')}
                </Text>
              </>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }
  return (
    <TouchableOpacity
      activeOpacity={props.allowAccountSelection ? 0.2 : 1}
      onPress={() => props.onSelect(props.userInfo)}
      testID={props.testID}
    >
      <View
        style={props.isNewUI ? styles.newContainer : styles.container}
        testID={TEST_IDS.CONTAINER}
      >
        <View style={styles.avatarView} testID={TEST_IDS.AVATAR_VIEW}>
          <SkillzAvatar source={props.userInfo.profilePic} size={48} />
        </View>
        <View style={styles.leftContainer}>
          <Text
            allowFontScaling={false}
            style={props.isNewUI ? styles.newUserName : styles.userName}
            testID={TEST_IDS.USER_NAME}
            numberOfLines={1}
            ellipsizeMode='tail'
          >
            {props.userInfo.userName}
          </Text>

          <View style={styles.currencyView}>
            <SkillzImage
              source={'cash_icon.svg'}
              height={props.isNewUI ? NEW_CASH_ICON_SIZE : CASH_ICON_SIZE}
              width={props.isNewUI ? NEW_CASH_ICON_SIZE : CASH_ICON_SIZE}
              style={props.isNewUI ? styles.newCurrencyIcon : styles.currencyIcon}
            />

            <Text
              allowFontScaling={false}
              style={props?.isNewUI ? styles.newCurrencyText : styles.currencyText}
            >{`$${props.userInfo.cashAmount}`}</Text>
          </View>
          {/* TODO: Uncomment when the following component is available */}
          {/* <CurrencyView
          amount={props.userInfo.cashAmount}
          currencyCode='USD'
          showIcon={true}
          textStyle={styles.currencyText}
          iconStyle={styles.currencyIcon}
        /> */}
        </View>

        <View style={styles.rightContainer}>
          {props.primary ? (
            <>
              <SkillzImage
                source={'checkmark.svg'}
                style={styles.primaryIcon}
                width={CHECK_MARK_ICON_SIZE}
                height={CHECK_MARK_ICON_SIZE}
              />
              <Text
                allowFontScaling={false}
                style={props?.isNewUI ? styles.newPrimaryText : styles.primaryText}
                testID={TEST_IDS.PRIMARY_TEXT}
              >
                {'PRIMARY'}
              </Text>
            </>
          ) : (
            <>
              <SkillzImage
                source={'mergeArrow.svg'}
                style={styles.mergeIcon}
                width={MERGE_ICON_SIZE}
                height={MERGE_ICON_SIZE}
              />
              <Text
                allowFontScaling={false}
                style={props?.isNewUI ? styles.newMergeText : styles.mergeText}
                testID={TEST_IDS.MERGE_TEXT}
              >
                {'MERGE'}
              </Text>
            </>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
}
